import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { equal, notEmpty } from '@ember/object/computed';
import { belongsTo, hasMany } from 'ember-data/relationships';
import { inject as service } from '@ember/service';
import { modelApiAction } from 'cio/utils/model-api-actions';
import DS from 'ember-data';
import { camelize, decamelize } from '@ember/string';
import ApiErrorsService from 'cio/services/api-errors';
import AccountModel from 'cio/models/account';
import { IdentifierKey, Identifiers, IdentifierState, IdentifierType, SelfServeMigrateTo } from 'cio/utils/identifiers';
import IdentityModel from 'cio/models/identity';
import UserModel from 'cio/models/user';
import { hexTrio, lightenOrDarkenColor } from 'cio/utils/color-functions';
import ObjectTypeModel from 'cio/models/object-type';
import {
  LogoSource,
  SerializedSubscriptionCenterSettings,
  SubscriptionCenterSettings,
} from 'cio/utils/subscription-center';
import { emptyValuesToNull, flatTransformKeys } from 'cio/utils/objects';

export enum MetricDenominator {
  Delivered = 'delivered',
  Sent = 'sent',
}

export enum DeliverySettings {
  Normal = 'normal',
  SendToOne = 'send_to_one',
  Off = 'off',
}

export interface SettingsEndpointResponse {
  settings: SettingsEndpointResponseSettings;
}

export interface SettingsEndpointResponseSettings {
  customer_columns: string[];
  object_type_columns: Record<string, string[]>;
  relationship_columns: Record<string, string[]>;
  metrics_timezone: string;
  journey_metrics_timezone: string;
  max_collection_size: number;
}

export interface IdentifierSettings {
  identify_by_id: boolean;
}

interface PageView {
  id: string;
}

interface PageViewsEndpointResponse {
  pages: PageView[];
}

export type PageViewsResponse = PageView[];

const DEFAULT_IDENTIFIER_SETTINGS: IdentifierSettings = {
  identify_by_id: true,
};

export default class EnvironmentModel extends Model {
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;

  @attr('string') name!: string;
  @attr('number') createdAt!: number;

  @attr('string') hexCode!: string;
  @attr('string', { defaultValue: 'normal' }) deliverySettings!: DeliverySettings;
  @attr('string', { defaultValue: '' }) deliveryEmail!: string;
  @attr('string') signingSecret!: string;
  @attr('array') sentAttributes!: string[];
  @attr('array') sentEvents!: string[];
  @attr('boolean') moreAttributes!: boolean;
  @attr('boolean') moreEvents!: boolean;
  @attr('string') identifierType!: IdentifierType;
  @attr('boolean', { defaultValue: true }) emailOpenTracking!: boolean;
  @attr() identifiers!: Identifiers;
  @attr('string', { defaultValue: MetricDenominator.Delivered }) metricDenominator!: MetricDenominator;
  @attr('string') languageAttribute!: string;
  @attr('array') deviceAttributes!: string[];
  @attr({ defaultValue: () => DEFAULT_IDENTIFIER_SETTINGS }) identifierSettings!: IdentifierSettings;
  @attr('string') identifiersMigrationInProgress!: string;
  @attr('string') shard!: string;
  @attr('boolean') subscriptionCenterEnabled!: boolean;
  @attr('number', { defaultValue: 2 }) maxAllowedObjectTypes!: number;
  @attr('boolean') canUpdateDefaultTemplateEngine!: boolean;
  @attr('number') defaultTemplateEngine!: number;

  @equal('identifiersMigrationInProgress', SelfServeMigrateTo.IdOnly) migratingToImmutableId!: boolean;
  @equal('identifiersMigrationInProgress', SelfServeMigrateTo.IdMutable) migratingToMutableId!: boolean;
  @equal('identifiersMigrationInProgress', SelfServeMigrateTo.MultiId) migratingToMultiId!: boolean;

  @notEmpty('identifiersMigrationInProgress') isMigrating!: boolean;
  @notEmpty('languageAttribute') hasLanguageAttribute!: boolean;

  @attr('array', {
    defaultValue() {
      return [];
    },
  })
  enabledActions!: string[];

  @attr('array', {
    defaultValue() {
      return [];
    },
  })
  pushProviders!: string[];

  @belongsTo('account', { async: false, inverse: 'environments' }) account!: AccountModel;

  @hasMany('user', { async: true, inverse: null }) users!: DS.PromiseManyArray<UserModel>;
  // (todo:swastik) identities should be defined only on the workspace model,
  // get rid of this later. It is used in a few places via the environment
  // service's current instance, and it will break those tests, so I've skipped
  // this for now.
  @hasMany('identity', { async: false, inverse: 'environment' }) identities!: IdentityModel[];
  @hasMany('object-type', { async: false, inverse: null }) objectTypes!: ObjectTypeModel[];

  @equal('deliverySettings', 'normal') normalDelivery!: boolean;
  @equal('deliverySettings', 'send_to_one') overriddenDelivery!: boolean;
  @equal('deliverySettings', 'off') disabledDelivery!: boolean;

  @equal('identifierType', IdentifierType.Email) isEmailIdentified!: boolean;
  @equal('identifierType', IdentifierType.MultiIdentifier) isMultiIdentifier!: boolean;

  // We do have the "multi identifier" identifier type, however, it can be
  // present for workspaces where "id" and "cio_id" are identifiers, but not
  // email.
  // So technically they allow multiple identifiers, but for practical purposes
  // we sometimes need to easily check for workspaces that allow id AND email (+
  // in future other identifiers)
  @computed('enabledIdentifiers.[]')
  get hasMultipleIdentifiers(): boolean {
    // NOTE: When we add support for other identifiers, this will need to change
    return this.isMultiIdentifier && this.canIdentifyById && this.canIdentifyByEmail;
  }

  _regenerateSigningSecret = modelApiAction({ path: 'signing_secret' });
  async regenerateSigningSecret() {
    let response = await this._regenerateSigningSecret({});
    this.store.pushPayload(response);
  }

  getSettings = modelApiAction<Record<string, never>, SettingsEndpointResponse>({
    type: 'GET',
    path: 'settings/workspace',
  });

  updateSetting = modelApiAction<{ settings: Partial<SettingsEndpointResponseSettings> }, SettingsEndpointResponse>({
    type: 'PUT',
    path: 'settings/workspace',
  });

  pageViews = modelApiAction<{ search: string }, PageViewsResponse>({
    type: 'GET',
    path: 'page_views',
    after(payload: PageViewsEndpointResponse) {
      return payload.pages ?? [];
    },
  });

  getSubscriptionCenter = modelApiAction<Record<string, never>, SubscriptionCenterSettings>({
    type: 'GET',
    path: 'subscription_center_settings',
    after(payload: SerializedSubscriptionCenterSettings) {
      return emptyValuesToNull<SubscriptionCenterSettings>(flatTransformKeys(payload, camelize));
    },
  });

  updateSubscriptionCenter = modelApiAction<SubscriptionCenterSettings, SubscriptionCenterSettings>({
    type: 'PUT',
    path: 'subscription_center_settings',
    before(payload: SubscriptionCenterSettings) {
      if (payload.logoSource === LogoSource.Account) {
        payload.logoPath = null;
      }

      return emptyValuesToNull(flatTransformKeys(payload, decamelize));
    },
    after(payload: SerializedSubscriptionCenterSettings) {
      return emptyValuesToNull<SubscriptionCenterSettings>(flatTransformKeys(payload, camelize));
    },
  });

  @computed('identities.@each.verified')
  get verifiedIdentities() {
    return this.identities.filter((identity) => identity.verified === 'verified');
  }

  @computed('deliveryEmail', 'normalDelivery', 'overriddenDelivery')
  get deliveryLabel() {
    if (this.normalDelivery) {
      return 'Sends normally';
    } else if (this.overriddenDelivery) {
      return `Sends to ${this.deliveryEmail}`;
    } else {
      return 'Does not send messages';
    }
  }

  @computed('hexCode')
  get hexCodeWithDefault() {
    // hexTrio returns $cio-green if the argument passed to it is an empty string or invalid hex code
    return `#${hexTrio(this.hexCode).join('')}`;
  }

  // We used the SASS function darken($cio-green, 6) before the workspaces visual customization feature
  @computed('hexCodeWithDefault')
  get darkRGBString() {
    return lightenOrDarkenColor(this.hexCodeWithDefault, -6);
  }

  @computed('identifiers.[]')
  get optionalIdentifiers(): IdentifierKey[] {
    return Object.keys(this.identifiers ?? {}).filter(
      (identifier) => identifier !== IdentifierKey.CioId,
    ) as IdentifierKey[];
  }

  @computed('identifiers.[]')
  get disabledIdentifiers(): IdentifierKey[] {
    return this.optionalIdentifiers.filter((identifier) => this.identifiers[identifier] === IdentifierState.Disabled);
  }

  @computed('identifiers.[]')
  get enabledIdentifiers(): IdentifierKey[] {
    return this.optionalIdentifiers.filter((identifier) => this.identifiers[identifier] !== IdentifierState.Disabled);
  }

  @computed('identifiers.[]')
  get allEnabledIdentifiers(): IdentifierKey[] {
    return (Object.keys(this.identifiers) as IdentifierKey[]).filter(
      (identifier) => this.identifiers[identifier] !== IdentifierState.Disabled,
    );
  }

  @computed('identifiers.[]')
  get immutableIdentifiers(): IdentifierKey[] {
    return this.optionalIdentifiers.filter(
      (identifier) => this.identifiers[identifier] === IdentifierState.EnabledImmutable,
    );
  }

  @computed('identifiers.[]')
  get mutableIdentifiers(): IdentifierKey[] {
    return this.optionalIdentifiers.filter((identifier) => this.identifiers[identifier] === IdentifierState.Enabled);
  }

  @computed('enabledIdentifiers.[]')
  get canIdentifyByEmail() {
    return this.enabledIdentifiers.includes(IdentifierKey.Email);
  }

  @computed('enabledIdentifiers.[]')
  get canIdentifyById() {
    return this.enabledIdentifiers.includes(IdentifierKey.Id);
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    environment: EnvironmentModel;
  }
}
